import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { searchBill, searchExpense, searchProduct, searchUser, searchStock, searchCashTransaction, searchStockTransaction } from '../../constant/Constant';
import {
    TrendingUp, TrendingDown, Users, IndianRupee, Calendar, Clock, AlertTriangle,
    CheckCircle, RefreshCw, Package, Scissors, Star, ShieldAlert, Gift, Target,
    BarChart3, PieChart as PieChartIcon, Activity, Zap, Sun, Moon, CloudRain,
    Thermometer, DollarSign, ShoppingBag, UserCheck, UserMinus, UserPlus, Percent,
    ArrowUpRight, ArrowDownRight, Sparkles, Brain, Eye, Wallet, CreditCard,
    CalendarDays, CalendarClock, TrendingUp as Trend, Award, AlertCircle,
    ChevronRight, Layers, Box, Users2, Banknote, Receipt, CircleDollarSign,
    Timer, CalendarRange, BarChart2, LineChart as LineChartIcon
} from 'lucide-react';
import {
    PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, LineChart, Line, Legend, Area, AreaChart, RadarChart,
    PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ComposedChart
} from 'recharts';
import toast from 'react-hot-toast';
import { getSelectedUnit } from '../../storage/Storage';

function BusinessForecasting() {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(true);
    const [allBills, setAllBills] = useState([]);
    const [allExpenses, setAllExpenses] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [allStaff, setAllStaff] = useState([]);
    const [allStock, setAllStock] = useState([]);
    const [cashTransactions, setCashTransactions] = useState([]);

    // Forecasting states
    const [revenueForecast, setRevenueForecast] = useState(null);
    const [clientForecast, setClientForecast] = useState(null);
    const [staffForecast, setStaffForecast] = useState(null);
    const [inventoryForecast, setInventoryForecast] = useState(null);
    const [serviceForecast, setServiceForecast] = useState(null);
    const [seasonalAnalysis, setSeasonalAnalysis] = useState(null);
    const [cashFlowForecast, setCashFlowForecast] = useState(null);
    const [riskAnalysis, setRiskAnalysis] = useState(null);
    const [keyMetrics, setKeyMetrics] = useState(null);

    // Active tab
    const [activeTab, setActiveTab] = useState('overview');

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Eye },
        { id: 'keymetrics', label: 'Key Metrics', icon: BarChart2 },
        { id: 'revenue', label: 'Revenue', icon: IndianRupee },
        { id: 'clients', label: 'Clients', icon: Users },
        { id: 'staff', label: 'Staff', icon: Users2 },
        { id: 'inventory', label: 'Inventory', icon: Package },
        { id: 'services', label: 'Services', icon: Scissors },
        { id: 'seasonal', label: 'Seasonal', icon: Sun },
        { id: 'cashflow', label: 'Cash Flow', icon: Wallet },
        { id: 'risks', label: 'Risk Analysis', icon: ShieldAlert },
    ];

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const unitId = getSelectedUnit()?._id;

            // Fetch all data in parallel
            const [billsRes, expensesRes, productsRes, staffRes, stockRes, cashRes] = await Promise.all([
                HitApi({ page: 1, limit: 100000, search: { unitId } }, searchBill),
                HitApi({ page: 1, limit: 100000, search: { unitId } }, searchExpense),
                HitApi({ page: 1, limit: 100000, search: { unitIds: unitId } }, searchProduct),
                HitApi({ page: 1, limit: 100000, search: { unitIds: unitId, userType: 'Staff' } }, searchUser),
                HitApi({ page: 1, limit: 100000, search: { unitId } }, searchStock).catch(() => ({ data: { docs: [] } })),
                HitApi({ page: 1, limit: 100000, search: { unitId } }, searchCashTransaction).catch(() => ({ data: { docs: [] } })),
            ]);

            const bills = billsRes?.data?.docs || [];
            const expenses = expensesRes?.data?.docs || [];
            const products = productsRes?.data?.docs || [];
            const staff = staffRes?.data?.docs || [];
            const stock = stockRes?.data?.docs || [];
            const cash = cashRes?.data?.docs || [];

            setAllBills(bills);
            setAllExpenses(expenses);
            setAllProducts(products);
            setAllStaff(staff);
            setAllStock(stock);
            setCashTransactions(cash);

            // Run all forecasting analyses
            analyzeRevenue(bills, expenses);
            analyzeClients(bills);
            analyzeStaff(bills, staff);
            analyzeInventory(bills, products, stock);
            analyzeServices(bills);
            analyzeSeasonalTrends(bills);
            analyzeCashFlow(bills, expenses, cash);
            analyzeRisks(bills, expenses, products, staff);
            analyzeKeyMetrics(bills, staff);

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error loading forecast data');
        } finally {
            setLoading(false);
        }
    };

    // ============= REVENUE FORECASTING =============
    const analyzeRevenue = (bills, expenses) => {
        const now = new Date();
        const monthlyRevenue = {};
        const weeklyRevenue = {};
        const dailyRevenue = {};

        // Process bills for revenue
        bills.forEach(bill => {
            const date = new Date(bill.timestamp || bill.createdAt);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const weekKey = getWeekNumber(date);
            const dayKey = date.toISOString().split('T')[0];

            const revenue = bill.calculations?.totals?.grandTotal || 0;

            monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + revenue;
            weeklyRevenue[weekKey] = (weeklyRevenue[weekKey] || 0) + revenue;
            dailyRevenue[dayKey] = (dailyRevenue[dayKey] || 0) + revenue;
        });

        // Calculate monthly trend
        const sortedMonths = Object.keys(monthlyRevenue).sort();
        const last6Months = sortedMonths.slice(-6);
        const monthlyTrend = last6Months.map(month => ({
            month: formatMonthLabel(month),
            revenue: monthlyRevenue[month],
            fullMonth: month
        }));

        // Linear regression for forecasting
        const revenueValues = last6Months.map(m => monthlyRevenue[m]);
        const avgGrowthRate = calculateGrowthRate(revenueValues);
        const lastMonthRevenue = revenueValues[revenueValues.length - 1] || 0;

        // Forecast next 3 months
        const forecast = [];
        for (let i = 1; i <= 3; i++) {
            const forecastDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const forecastMonth = `${forecastDate.getFullYear()}-${String(forecastDate.getMonth() + 1).padStart(2, '0')}`;
            const forecastValue = lastMonthRevenue * Math.pow(1 + avgGrowthRate, i);
            forecast.push({
                month: formatMonthLabel(forecastMonth),
                revenue: Math.round(forecastValue),
                isForecast: true
            });
        }

        // Calculate metrics
        const totalRevenue = Object.values(monthlyRevenue).reduce((a, b) => a + b, 0);
        const avgMonthlyRevenue = totalRevenue / Math.max(sortedMonths.length, 1);
        const currentMonthRevenue = monthlyRevenue[`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`] || 0;
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthKey = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
        const previousMonthRevenue = monthlyRevenue[lastMonthKey] || 0;

        // Calculate expense impact
        const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
        const netProfit = totalRevenue - totalExpenses;
        const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

        setRevenueForecast({
            monthlyTrend: [...monthlyTrend, ...forecast],
            totalRevenue,
            avgMonthlyRevenue,
            currentMonthRevenue,
            previousMonthRevenue,
            growthRate: avgGrowthRate * 100,
            monthOverMonthGrowth: previousMonthRevenue > 0 ? ((currentMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100 : 0,
            forecastedNextMonth: forecast[0]?.revenue || 0,
            forecastedQuarter: forecast.reduce((a, b) => a + b.revenue, 0),
            totalExpenses,
            netProfit,
            profitMargin,
            revenueByPaymentMethod: calculatePaymentMethodBreakdown(bills),
            revenueByCategory: calculateRevenueByCategory(bills)
        });
    };

    // ============= CLIENT FORECASTING =============
    const analyzeClients = (bills) => {
        const clientMap = new Map();
        const now = new Date();
        const monthlyNewClients = {};
        const monthlyReturningClients = {};

        bills.forEach(bill => {
            const client = bill.client;
            if (!client) return;

            const clientId = client._id || client.phoneNumber;
            const billDate = new Date(bill.timestamp || bill.createdAt);
            const monthKey = `${billDate.getFullYear()}-${String(billDate.getMonth() + 1).padStart(2, '0')}`;

            if (!clientMap.has(clientId)) {
                clientMap.set(clientId, {
                    id: clientId,
                    name: client.name,
                    firstVisit: billDate,
                    lastVisit: billDate,
                    totalVisits: 1,
                    totalSpent: bill.calculations?.totals?.grandTotal || 0,
                    visitDates: [billDate]
                });
                monthlyNewClients[monthKey] = (monthlyNewClients[monthKey] || 0) + 1;
            } else {
                const existing = clientMap.get(clientId);
                existing.totalVisits++;
                existing.totalSpent += bill.calculations?.totals?.grandTotal || 0;
                existing.visitDates.push(billDate);
                if (billDate > existing.lastVisit) existing.lastVisit = billDate;
                if (billDate < existing.firstVisit) existing.firstVisit = billDate;
                monthlyReturningClients[monthKey] = (monthlyReturningClients[monthKey] || 0) + 1;
            }
        });

        const clients = Array.from(clientMap.values());

        // Calculate churn risk
        const churnAnalysis = clients.map(client => {
            const daysSinceLastVisit = Math.floor((now - new Date(client.lastVisit)) / (1000 * 60 * 60 * 24));
            const avgVisitGap = calculateAverageVisitGap(client.visitDates);
            let churnRisk = 'low';
            let churnScore = 0;

            if (avgVisitGap) {
                if (daysSinceLastVisit > avgVisitGap * 2) {
                    churnRisk = 'high';
                    churnScore = 80 + Math.min(20, (daysSinceLastVisit - avgVisitGap * 2) / 10);
                } else if (daysSinceLastVisit > avgVisitGap * 1.5) {
                    churnRisk = 'medium';
                    churnScore = 40 + Math.min(40, (daysSinceLastVisit - avgVisitGap * 1.5) / 5);
                } else {
                    churnScore = Math.min(40, daysSinceLastVisit / avgVisitGap * 40);
                }
            } else if (daysSinceLastVisit > 60) {
                churnRisk = 'high';
                churnScore = 70;
            } else if (daysSinceLastVisit > 30) {
                churnRisk = 'medium';
                churnScore = 50;
            } else {
                churnScore = daysSinceLastVisit;
            }

            return { ...client, daysSinceLastVisit, avgVisitGap, churnRisk, churnScore: Math.round(churnScore) };
        });

        const highRiskClients = churnAnalysis.filter(c => c.churnRisk === 'high');
        const mediumRiskClients = churnAnalysis.filter(c => c.churnRisk === 'medium');
        const lowRiskClients = churnAnalysis.filter(c => c.churnRisk === 'low');

        // Client segments
        const loyalClients = clients.filter(c => c.totalVisits >= 10);
        const regularClients = clients.filter(c => c.totalVisits >= 5 && c.totalVisits < 10);
        const occasionalClients = clients.filter(c => c.totalVisits >= 2 && c.totalVisits < 5);
        const oneTimeClients = clients.filter(c => c.totalVisits === 1);

        // Forecast new clients
        const sortedMonths = Object.keys(monthlyNewClients).sort();
        const last6MonthsNew = sortedMonths.slice(-6).map(m => monthlyNewClients[m] || 0);
        const avgNewClientsPerMonth = last6MonthsNew.reduce((a, b) => a + b, 0) / Math.max(last6MonthsNew.length, 1);
        const newClientGrowthRate = calculateGrowthRate(last6MonthsNew);

        // Client lifetime value
        const avgLTV = clients.length > 0 ? clients.reduce((sum, c) => sum + c.totalSpent, 0) / clients.length : 0;
        const avgVisitsPerClient = clients.length > 0 ? clients.reduce((sum, c) => sum + c.totalVisits, 0) / clients.length : 0;

        // Monthly trend data
        const clientTrend = sortedMonths.slice(-6).map(month => ({
            month: formatMonthLabel(month),
            newClients: monthlyNewClients[month] || 0,
            returningClients: monthlyReturningClients[month] || 0
        }));

        // Forecast next 3 months
        for (let i = 1; i <= 3; i++) {
            const forecastDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const forecastMonth = formatMonthLabel(`${forecastDate.getFullYear()}-${String(forecastDate.getMonth() + 1).padStart(2, '0')}`);
            clientTrend.push({
                month: forecastMonth,
                newClients: Math.round(avgNewClientsPerMonth * Math.pow(1 + newClientGrowthRate, i)),
                returningClients: null,
                isForecast: true
            });
        }

        setClientForecast({
            totalClients: clients.length,
            avgLTV,
            avgVisitsPerClient,
            churnAnalysis: {
                highRisk: highRiskClients.length,
                mediumRisk: mediumRiskClients.length,
                lowRisk: lowRiskClients.length,
                highRiskClients: highRiskClients.slice(0, 10),
                potentialRevenueLoss: highRiskClients.reduce((sum, c) => sum + (c.totalSpent / c.totalVisits), 0)
            },
            segments: {
                loyal: loyalClients.length,
                regular: regularClients.length,
                occasional: occasionalClients.length,
                oneTime: oneTimeClients.length
            },
            retentionRate: clients.length > 0 ? ((clients.length - oneTimeClients.length) / clients.length) * 100 : 0,
            avgNewClientsPerMonth,
            newClientGrowthRate: newClientGrowthRate * 100,
            forecastedNewClients: Math.round(avgNewClientsPerMonth * (1 + newClientGrowthRate)),
            clientTrend
        });
    };

    // ============= STAFF FORECASTING =============
    const analyzeStaff = (bills, staff) => {
        const staffPerformance = new Map();

        // Create staff lookup map for name resolution
        const staffMap = new Map();
        (staff || []).forEach(s => {
            if (s._id) staffMap.set(s._id, s);
            if (s.name) staffMap.set(s.name, s);
        });

        // Helper to get staff name
        const getStaffName = (staffRef) => {
            if (!staffRef) return 'Unknown';
            if (typeof staffRef === 'string') {
                const staffDetails = staffMap.get(staffRef);
                return staffDetails?.name || 'Deleted Staff';
            }
            if (typeof staffRef === 'object') {
                if (staffRef.name) return staffRef.name;
                if (staffRef._id) {
                    const staffDetails = staffMap.get(staffRef._id);
                    return staffDetails?.name || 'Deleted Staff';
                }
            }
            return 'Unknown';
        };

        // Helper to get staff ID
        const getStaffId = (staffRef) => {
            if (!staffRef) return null;
            if (typeof staffRef === 'string') return staffRef;
            return staffRef._id || staffRef.name || null;
        };

        bills.forEach(bill => {
            const services = bill.services || [];
            const products = bill.products || [];

            [...services, ...products].forEach(item => {
                const staffMember = item.staff;
                if (!staffMember) return;

                const staffId = getStaffId(staffMember);
                if (!staffId) return;

                const staffName = getStaffName(staffMember);
                const revenue = item.pricing?.finalPrice || item.pricing?.totalPrice || 0;

                if (!staffPerformance.has(staffId)) {
                    staffPerformance.set(staffId, {
                        id: staffId,
                        name: staffName,
                        totalRevenue: 0,
                        servicesCount: 0,
                        productsCount: 0,
                        billsCount: 0,
                        dailyRevenue: {}
                    });
                }

                const perf = staffPerformance.get(staffId);
                perf.totalRevenue += revenue;
                if (item.pricing?.basePrice !== undefined && !item.productName) {
                    perf.servicesCount++;
                } else {
                    perf.productsCount++;
                }

                const dateKey = new Date(bill.timestamp || bill.createdAt).toISOString().split('T')[0];
                perf.dailyRevenue[dateKey] = (perf.dailyRevenue[dateKey] || 0) + revenue;
            });
        });

        const performanceArray = Array.from(staffPerformance.values());

        // Calculate staff metrics
        const staffMetrics = performanceArray.map(s => {
            const dailyValues = Object.values(s.dailyRevenue);
            const avgDailyRevenue = dailyValues.length > 0 ? dailyValues.reduce((a, b) => a + b, 0) / dailyValues.length : 0;
            const productivity = s.servicesCount + s.productsCount;
            const revenuePerService = s.servicesCount > 0 ? s.totalRevenue / s.servicesCount : 0;

            // Trend analysis
            const sortedDays = Object.keys(s.dailyRevenue).sort();
            const last7Days = sortedDays.slice(-7).map(d => s.dailyRevenue[d]);
            const previous7Days = sortedDays.slice(-14, -7).map(d => s.dailyRevenue[d]);
            const weeklyTrend = last7Days.length > 0 && previous7Days.length > 0
                ? ((last7Days.reduce((a, b) => a + b, 0) - previous7Days.reduce((a, b) => a + b, 0)) / Math.max(previous7Days.reduce((a, b) => a + b, 0), 1)) * 100
                : 0;

            return {
                ...s,
                avgDailyRevenue,
                productivity,
                revenuePerService,
                weeklyTrend,
                workingDays: dailyValues.length
            };
        }).sort((a, b) => b.totalRevenue - a.totalRevenue);

        // Top performers
        const topPerformers = staffMetrics.slice(0, 5);
        const underperformers = staffMetrics.filter(s => s.weeklyTrend < -20);

        // Calculate team metrics
        const totalTeamRevenue = staffMetrics.reduce((sum, s) => sum + s.totalRevenue, 0);
        const avgRevenuePerStaff = staffMetrics.length > 0 ? totalTeamRevenue / staffMetrics.length : 0;
        const totalServices = staffMetrics.reduce((sum, s) => sum + s.servicesCount, 0);
        const totalProducts = staffMetrics.reduce((sum, s) => sum + s.productsCount, 0);

        // Forecast staff requirements
        const avgServicesPerDay = totalServices / Math.max(Object.keys(staffMetrics[0]?.dailyRevenue || {}).length, 1);
        const forecastedDailyServices = avgServicesPerDay * 1.1; // 10% growth assumption
        const optimalStaffCount = Math.ceil(forecastedDailyServices / 8); // Assuming 8 services per staff per day

        setStaffForecast({
            staffMetrics,
            topPerformers,
            underperformers,
            totalTeamRevenue,
            avgRevenuePerStaff,
            totalServices,
            totalProducts,
            optimalStaffCount,
            currentStaffCount: staff.length,
            staffingRecommendation: optimalStaffCount > staff.length ? 'Hire more staff' : optimalStaffCount < staff.length ? 'Consider reducing staff' : 'Optimal staffing',
            performanceDistribution: [
                { name: 'High Performers', value: staffMetrics.filter(s => s.totalRevenue > avgRevenuePerStaff * 1.2).length },
                { name: 'Average', value: staffMetrics.filter(s => s.totalRevenue >= avgRevenuePerStaff * 0.8 && s.totalRevenue <= avgRevenuePerStaff * 1.2).length },
                { name: 'Below Average', value: staffMetrics.filter(s => s.totalRevenue < avgRevenuePerStaff * 0.8).length }
            ]
        });
    };

    // ============= INVENTORY FORECASTING =============
    const analyzeInventory = (bills, products, stock) => {
        const productUsage = new Map();
        const monthlyUsage = {};

        // Analyze product usage from bills
        bills.forEach(bill => {
            const billProducts = bill.products || [];
            const date = new Date(bill.timestamp || bill.createdAt);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            billProducts.forEach(product => {
                const productId = product._id || product.productName;
                const productName = product.productName || product.name;
                const quantity = product.quantity || 1;

                if (!productUsage.has(productId)) {
                    productUsage.set(productId, {
                        id: productId,
                        name: productName,
                        totalSold: 0,
                        totalRevenue: 0,
                        monthlySales: {}
                    });
                }

                const usage = productUsage.get(productId);
                usage.totalSold += quantity;
                usage.totalRevenue += product.pricing?.finalPrice || 0;
                usage.monthlySales[monthKey] = (usage.monthlySales[monthKey] || 0) + quantity;

                monthlyUsage[monthKey] = (monthlyUsage[monthKey] || 0) + quantity;
            });
        });

        const productArray = Array.from(productUsage.values());

        // Calculate demand forecast for each product
        const productForecast = productArray.map(product => {
            const sortedMonths = Object.keys(product.monthlySales).sort();
            const last3Months = sortedMonths.slice(-3).map(m => product.monthlySales[m] || 0);
            const avgMonthlySales = last3Months.reduce((a, b) => a + b, 0) / Math.max(last3Months.length, 1);
            const growthRate = calculateGrowthRate(last3Months);

            // Find current stock level
            const stockItem = stock.find(s => s.productId === product.id || s.productName === product.name);
            const currentStock = stockItem?.quantity || 0;

            // Calculate reorder point (1.5 months of average sales as safety stock)
            const reorderPoint = Math.ceil(avgMonthlySales * 1.5);
            const forecastedDemand = Math.round(avgMonthlySales * (1 + growthRate));

            return {
                ...product,
                avgMonthlySales,
                growthRate: growthRate * 100,
                currentStock,
                reorderPoint,
                forecastedDemand,
                stockStatus: currentStock <= 0 ? 'Out of Stock' :
                    currentStock < reorderPoint ? 'Low Stock' : 'In Stock',
                daysOfStock: avgMonthlySales > 0 ? Math.round((currentStock / avgMonthlySales) * 30) : 999
            };
        }).sort((a, b) => b.totalSold - a.totalSold);

        // Top selling products
        const topProducts = productForecast.slice(0, 10);
        const lowStockProducts = productForecast.filter(p => p.stockStatus === 'Low Stock' || p.stockStatus === 'Out of Stock');
        const fastMoving = productForecast.filter(p => p.avgMonthlySales > 10);
        const slowMoving = productForecast.filter(p => p.avgMonthlySales < 2 && p.totalSold > 0);

        // Monthly trend
        const sortedMonths = Object.keys(monthlyUsage).sort();
        const inventoryTrend = sortedMonths.slice(-6).map(month => ({
            month: formatMonthLabel(month),
            usage: monthlyUsage[month]
        }));

        // Calculate inventory value
        const totalInventoryValue = products.reduce((sum, p) => {
            const stockItem = stock.find(s => s.productId === p._id);
            return sum + (stockItem?.quantity || 0) * (p.costPrice || 0);
        }, 0);

        setInventoryForecast({
            productForecast,
            topProducts,
            lowStockProducts,
            fastMoving,
            slowMoving,
            inventoryTrend,
            totalProducts: products.length,
            totalInventoryValue,
            lowStockCount: lowStockProducts.length,
            outOfStockCount: productForecast.filter(p => p.stockStatus === 'Out of Stock').length,
            reorderRecommendations: lowStockProducts.map(p => ({
                product: p.name,
                currentStock: p.currentStock,
                recommended: Math.max(p.reorderPoint * 2 - p.currentStock, 0),
                urgency: p.stockStatus === 'Out of Stock' ? 'Critical' : 'High'
            }))
        });
    };

    // ============= SERVICE FORECASTING =============
    const analyzeServices = (bills) => {
        const serviceUsage = new Map();
        const monthlyServiceUsage = {};

        bills.forEach(bill => {
            const services = bill.services || [];
            const date = new Date(bill.timestamp || bill.createdAt);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            services.forEach(service => {
                const serviceName = (service.name || service.serviceName || 'Unknown Service').trim();
                const quantity = service.quantity || 1;
                const revenue = service.pricing?.finalPrice || service.pricing?.totalPrice || 0;

                // Use service NAME as key to combine all same-named services
                if (!serviceUsage.has(serviceName)) {
                    serviceUsage.set(serviceName, {
                        id: serviceName,
                        name: serviceName,
                        totalCount: 0,
                        totalRevenue: 0,
                        monthlyCounts: {},
                        avgDiscount: 0,
                        discountSum: 0
                    });
                }

                const usage = serviceUsage.get(serviceName);
                usage.totalCount += quantity;
                usage.totalRevenue += revenue;
                usage.monthlyCounts[monthKey] = (usage.monthlyCounts[monthKey] || 0) + quantity;
                usage.discountSum += (service.pricing?.basePrice || 0) - (service.pricing?.finalPrice || 0);

                monthlyServiceUsage[monthKey] = (monthlyServiceUsage[monthKey] || 0) + quantity;
            });
        });

        const serviceArray = Array.from(serviceUsage.values()).map(service => {
            const sortedMonths = Object.keys(service.monthlyCounts).sort();
            const last3Months = sortedMonths.slice(-3).map(m => service.monthlyCounts[m] || 0);
            const avgMonthlyCount = last3Months.reduce((a, b) => a + b, 0) / Math.max(last3Months.length, 1);
            const growthRate = calculateGrowthRate(last3Months);

            return {
                ...service,
                avgMonthlyCount,
                avgPrice: service.totalCount > 0 ? service.totalRevenue / service.totalCount : 0,
                avgDiscount: service.totalCount > 0 ? service.discountSum / service.totalCount : 0,
                growthRate: growthRate * 100,
                forecastedDemand: Math.round(avgMonthlyCount * (1 + growthRate)),
                popularity: service.totalCount
            };
        }).sort((a, b) => b.totalRevenue - a.totalRevenue);

        // Top services
        const topServices = serviceArray.slice(0, 10);
        const growingServices = serviceArray.filter(s => s.growthRate > 10).sort((a, b) => b.growthRate - a.growthRate);
        const decliningServices = serviceArray.filter(s => s.growthRate < -10).sort((a, b) => a.growthRate - b.growthRate);

        // Monthly trend
        const sortedMonths = Object.keys(monthlyServiceUsage).sort();
        const serviceTrend = sortedMonths.slice(-6).map(month => ({
            month: formatMonthLabel(month),
            count: monthlyServiceUsage[month]
        }));

        // Service breakdown by individual service names (top services by revenue)
        const categoryBreakdown = serviceArray
            .slice(0, 10) // Top 10 services
            .map(service => ({
                name: service.name || 'Unknown Service',
                value: service.totalRevenue
            }));

        const totalServicesRendered = serviceArray.reduce((sum, s) => sum + s.totalCount, 0);

        // Service usage breakdown by count (most popular services)
        const serviceUsageBreakdown = serviceArray
            .sort((a, b) => b.totalCount - a.totalCount)
            .slice(0, 10)
            .map(service => ({
                name: service.name || 'Unknown Service',
                count: service.totalCount,
                percentage: totalServicesRendered > 0 ? ((service.totalCount / totalServicesRendered) * 100).toFixed(1) : 0
            }));

        setServiceForecast({
            serviceArray,
            topServices,
            growingServices,
            decliningServices,
            serviceTrend,
            totalServices: serviceArray.length,
            totalServicesRendered,
            totalServiceRevenue: serviceArray.reduce((sum, s) => sum + s.totalRevenue, 0),
            avgServicePrice: serviceArray.length > 0 ? serviceArray.reduce((sum, s) => sum + s.avgPrice, 0) / serviceArray.length : 0,
            categoryBreakdown,
            serviceUsageBreakdown,
            recommendations: {
                promote: growingServices.slice(0, 3).map(s => s.name),
                review: decliningServices.slice(0, 3).map(s => s.name),
                bundle: topServices.slice(0, 2).map(s => s.name)
            }
        });
    };

    // ============= SEASONAL ANALYSIS =============
    const analyzeSeasonalTrends = (bills) => {
        const hourlyData = Array(24).fill(0);
        const dailyData = Array(7).fill(0);
        const monthlyData = Array(12).fill(0);
        const hourlyRevenue = Array(24).fill(0);
        const dailyRevenue = Array(7).fill(0);
        const monthlyRevenue = Array(12).fill(0);

        bills.forEach(bill => {
            const date = new Date(bill.timestamp || bill.createdAt);
            const hour = date.getHours();
            const day = date.getDay();
            const month = date.getMonth();
            const revenue = bill.calculations?.totals?.grandTotal || 0;

            hourlyData[hour]++;
            dailyData[day]++;
            monthlyData[month]++;
            hourlyRevenue[hour] += revenue;
            dailyRevenue[day] += revenue;
            monthlyRevenue[month] += revenue;
        });

        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        // Find peak times
        const peakHour = hourlyData.indexOf(Math.max(...hourlyData));
        const peakDay = dailyData.indexOf(Math.max(...dailyData));
        const peakMonth = monthlyData.indexOf(Math.max(...monthlyData));

        // Format data for charts
        const hourlyTrend = hourlyData.map((count, index) => ({
            hour: `${index}:00`,
            visits: count,
            revenue: hourlyRevenue[index]
        }));

        const dailyTrend = dailyData.map((count, index) => ({
            day: dayNames[index],
            visits: count,
            revenue: dailyRevenue[index]
        }));

        const monthlyTrend = monthlyData.map((count, index) => ({
            month: monthNames[index],
            visits: count,
            revenue: monthlyRevenue[index]
        }));

        // Calculate busy vs slow periods
        const avgHourlyVisits = hourlyData.reduce((a, b) => a + b, 0) / 24;
        const busyHours = hourlyData.map((count, index) => ({ hour: index, count }))
            .filter(h => h.count > avgHourlyVisits * 1.2)
            .map(h => `${h.hour}:00`);

        const slowHours = hourlyData.map((count, index) => ({ hour: index, count }))
            .filter(h => h.count < avgHourlyVisits * 0.5 && h.count > 0)
            .map(h => `${h.hour}:00`);

        setSeasonalAnalysis({
            hourlyTrend,
            dailyTrend,
            monthlyTrend,
            peakHour: `${peakHour}:00`,
            peakDay: dayNames[peakDay],
            peakMonth: monthNames[peakMonth],
            busyHours,
            slowHours,
            recommendations: {
                staffingTip: `Increase staff during ${peakHour}:00 - ${peakHour + 2}:00 on ${dayNames[peakDay]}s`,
                promotionTip: `Run promotions during ${slowHours.slice(0, 2).join(', ')} to boost traffic`,
                peakSeasonTip: `Prepare for high demand in ${monthNames[peakMonth]}`
            }
        });
    };

    // ============= CASH FLOW FORECASTING =============
    const analyzeCashFlow = (bills, expenses, cashTransactions) => {
        const now = new Date();
        const dailyCashFlow = {};
        const monthlyCashFlow = {};

        // Process income from bills
        bills.forEach(bill => {
            const date = new Date(bill.timestamp || bill.createdAt);
            const dayKey = date.toISOString().split('T')[0];
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            const income = bill.payment?.totalPaid || 0;

            if (!dailyCashFlow[dayKey]) {
                dailyCashFlow[dayKey] = { income: 0, expense: 0 };
            }
            dailyCashFlow[dayKey].income += income;

            if (!monthlyCashFlow[monthKey]) {
                monthlyCashFlow[monthKey] = { income: 0, expense: 0 };
            }
            monthlyCashFlow[monthKey].income += income;
        });

        // Process expenses
        expenses.forEach(expense => {
            const date = new Date(expense.createdAt || expense.date);
            const dayKey = date.toISOString().split('T')[0];
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            const amount = expense.amount || 0;

            if (!dailyCashFlow[dayKey]) {
                dailyCashFlow[dayKey] = { income: 0, expense: 0 };
            }
            dailyCashFlow[dayKey].expense += amount;

            if (!monthlyCashFlow[monthKey]) {
                monthlyCashFlow[monthKey] = { income: 0, expense: 0 };
            }
            monthlyCashFlow[monthKey].expense += amount;
        });

        // Calculate monthly trend
        const sortedMonths = Object.keys(monthlyCashFlow).sort();
        const cashFlowTrend = sortedMonths.slice(-6).map(month => ({
            month: formatMonthLabel(month),
            income: monthlyCashFlow[month].income,
            expense: monthlyCashFlow[month].expense,
            netFlow: monthlyCashFlow[month].income - monthlyCashFlow[month].expense
        }));

        // Calculate metrics
        const totalIncome = Object.values(monthlyCashFlow).reduce((sum, m) => sum + m.income, 0);
        const totalExpense = Object.values(monthlyCashFlow).reduce((sum, m) => sum + m.expense, 0);
        const avgMonthlyIncome = totalIncome / Math.max(sortedMonths.length, 1);
        const avgMonthlyExpense = totalExpense / Math.max(sortedMonths.length, 1);

        // Forecast next 3 months
        const incomeValues = sortedMonths.slice(-6).map(m => monthlyCashFlow[m].income);
        const expenseValues = sortedMonths.slice(-6).map(m => monthlyCashFlow[m].expense);
        const incomeGrowthRate = calculateGrowthRate(incomeValues);
        const expenseGrowthRate = calculateGrowthRate(expenseValues);

        const forecast = [];
        let runningBalance = totalIncome - totalExpense;

        for (let i = 1; i <= 3; i++) {
            const forecastDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const forecastMonth = formatMonthLabel(`${forecastDate.getFullYear()}-${String(forecastDate.getMonth() + 1).padStart(2, '0')}`);
            const forecastedIncome = avgMonthlyIncome * Math.pow(1 + incomeGrowthRate, i);
            const forecastedExpense = avgMonthlyExpense * Math.pow(1 + expenseGrowthRate, i);
            const netFlow = forecastedIncome - forecastedExpense;
            runningBalance += netFlow;

            forecast.push({
                month: forecastMonth,
                income: Math.round(forecastedIncome),
                expense: Math.round(forecastedExpense),
                netFlow: Math.round(netFlow),
                projectedBalance: Math.round(runningBalance),
                isForecast: true
            });
        }

        // Payment method distribution
        const paymentMethods = { cash: 0, upi: 0, card: 0, wallet: 0, other: 0 };
        bills.forEach(bill => {
            const methods = bill.payment?.methods || {};
            Object.keys(methods).forEach(method => {
                if (paymentMethods.hasOwnProperty(method)) {
                    paymentMethods[method] += methods[method] || 0;
                }
            });
        });

        setCashFlowForecast({
            cashFlowTrend: [...cashFlowTrend, ...forecast],
            totalIncome,
            totalExpense,
            netCashFlow: totalIncome - totalExpense,
            avgMonthlyIncome,
            avgMonthlyExpense,
            avgMonthlyNetFlow: avgMonthlyIncome - avgMonthlyExpense,
            incomeGrowthRate: incomeGrowthRate * 100,
            expenseGrowthRate: expenseGrowthRate * 100,
            forecastedNetFlow: forecast.reduce((sum, f) => sum + f.netFlow, 0),
            paymentMethodDistribution: Object.entries(paymentMethods)
                .filter(([_, value]) => value > 0)
                .map(([name, value]) => ({
                    name: name.charAt(0).toUpperCase() + name.slice(1),
                    value
                })),
            healthStatus: (avgMonthlyIncome - avgMonthlyExpense) > 0 ? 'Healthy' : 'At Risk'
        });
    };

    // ============= RISK ANALYSIS =============
    const analyzeRisks = (bills, expenses, products, staff) => {
        const risks = [];
        const now = new Date();

        // 1. Revenue concentration risk
        const clientRevenue = new Map();
        bills.forEach(bill => {
            const clientId = bill.client?._id || bill.client?.phoneNumber;
            if (clientId) {
                clientRevenue.set(clientId, (clientRevenue.get(clientId) || 0) + (bill.calculations?.totals?.grandTotal || 0));
            }
        });

        const sortedClientRevenue = Array.from(clientRevenue.values()).sort((a, b) => b - a);
        const totalRev = sortedClientRevenue.reduce((a, b) => a + b, 0);
        const top10ClientsRevenue = sortedClientRevenue.slice(0, 10).reduce((a, b) => a + b, 0);
        const concentrationRisk = totalRev > 0 ? (top10ClientsRevenue / totalRev) * 100 : 0;

        if (concentrationRisk > 50) {
            risks.push({
                type: 'Revenue Concentration',
                severity: 'High',
                description: `Top 10 clients contribute ${concentrationRisk.toFixed(1)}% of revenue`,
                recommendation: 'Diversify client base and implement client acquisition strategies',
                icon: 'users'
            });
        }

        // 2. Staff dependency risk
        const staffRevenue = new Map();
        bills.forEach(bill => {
            [...(bill.services || []), ...(bill.products || [])].forEach(item => {
                const staffId = item.staff?._id;
                if (staffId) {
                    staffRevenue.set(staffId, (staffRevenue.get(staffId) || 0) + (item.pricing?.finalPrice || 0));
                }
            });
        });

        const sortedStaffRevenue = Array.from(staffRevenue.values()).sort((a, b) => b - a);
        const totalStaffRev = sortedStaffRevenue.reduce((a, b) => a + b, 0);
        const topStaffRevenue = sortedStaffRevenue[0] || 0;
        const staffDependencyRisk = totalStaffRev > 0 ? (topStaffRevenue / totalStaffRev) * 100 : 0;

        if (staffDependencyRisk > 40) {
            risks.push({
                type: 'Staff Dependency',
                severity: 'Medium',
                description: `Top performer contributes ${staffDependencyRisk.toFixed(1)}% of revenue`,
                recommendation: 'Cross-train staff and distribute workload more evenly',
                icon: 'users2'
            });
        }

        // 3. Payment collection risk
        const unpaidAmount = bills.reduce((sum, bill) => {
            const remaining = bill.payment?.remainingAmount || (
                (bill.calculations?.totals?.grandTotal || 0) - (bill.payment?.totalPaid || 0)
            );
            return sum + (remaining > 0 ? remaining : 0);
        }, 0);

        const unpaidPercentage = totalRev > 0 ? (unpaidAmount / totalRev) * 100 : 0;

        if (unpaidPercentage > 10) {
            risks.push({
                type: 'Payment Collection',
                severity: unpaidPercentage > 20 ? 'High' : 'Medium',
                description: `${unpaidPercentage.toFixed(1)}% of revenue is unpaid (${formatCurrency(unpaidAmount)})`,
                recommendation: 'Implement stricter payment policies and follow-up procedures',
                icon: 'wallet'
            });
        }

        // 4. Inventory risk
        const outOfStockCount = products.filter(p => {
            // Simplified check - in real scenario, cross-reference with stock
            return false; // Would need actual stock data
        }).length;

        // 5. Seasonal variance risk
        const monthlyRevenue = {};
        bills.forEach(bill => {
            const date = new Date(bill.timestamp || bill.createdAt);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + (bill.calculations?.totals?.grandTotal || 0);
        });

        const revenueValues = Object.values(monthlyRevenue);
        if (revenueValues.length > 3) {
            const avgRevenue = revenueValues.reduce((a, b) => a + b, 0) / revenueValues.length;
            const variance = revenueValues.reduce((sum, val) => sum + Math.pow(val - avgRevenue, 2), 0) / revenueValues.length;
            const stdDev = Math.sqrt(variance);
            const coefficientOfVariation = avgRevenue > 0 ? (stdDev / avgRevenue) * 100 : 0;

            if (coefficientOfVariation > 30) {
                risks.push({
                    type: 'Revenue Volatility',
                    severity: coefficientOfVariation > 50 ? 'High' : 'Medium',
                    description: `Revenue varies by ${coefficientOfVariation.toFixed(1)}% month-to-month`,
                    recommendation: 'Develop membership programs and recurring revenue streams',
                    icon: 'trending'
                });
            }
        }

        // 6. Client churn risk
        const recentBills = bills.filter(bill => {
            const date = new Date(bill.timestamp || bill.createdAt);
            return (now - date) / (1000 * 60 * 60 * 24) <= 90;
        });
        const recentClients = new Set(recentBills.map(b => b.client?._id || b.client?.phoneNumber).filter(Boolean));
        const totalClients = new Set(bills.map(b => b.client?._id || b.client?.phoneNumber).filter(Boolean));
        const inactiveRate = totalClients.size > 0 ? ((totalClients.size - recentClients.size) / totalClients.size) * 100 : 0;

        if (inactiveRate > 40) {
            risks.push({
                type: 'Client Churn',
                severity: inactiveRate > 60 ? 'High' : 'Medium',
                description: `${inactiveRate.toFixed(1)}% of clients inactive in last 90 days`,
                recommendation: 'Implement re-engagement campaigns and loyalty programs',
                icon: 'userMinus'
            });
        }

        // Calculate overall risk score
        const severityScores = { 'High': 30, 'Medium': 15, 'Low': 5 };
        const totalRiskScore = risks.reduce((sum, risk) => sum + severityScores[risk.severity], 0);
        const maxPossibleScore = 150; // 5 high severity risks
        const riskPercentage = (totalRiskScore / maxPossibleScore) * 100;

        let overallRiskLevel = 'Low';
        if (riskPercentage > 60) overallRiskLevel = 'High';
        else if (riskPercentage > 30) overallRiskLevel = 'Medium';

        setRiskAnalysis({
            risks,
            overallRiskLevel,
            riskScore: Math.round(riskPercentage),
            metrics: {
                concentrationRisk,
                staffDependencyRisk,
                unpaidPercentage,
                inactiveRate
            },
            opportunities: [
                { title: 'Membership Programs', potential: 'High', description: 'Convert regular clients to members for recurring revenue' },
                { title: 'Product Upselling', potential: 'Medium', description: 'Train staff to recommend complementary products' },
                { title: 'Off-Peak Promotions', potential: 'Medium', description: 'Drive traffic during slow hours with special offers' },
                { title: 'Referral Program', potential: 'High', description: 'Incentivize existing clients to bring new customers' }
            ]
        });
    };

    // ============= KEY METRICS ANALYSIS =============
    const analyzeKeyMetrics = (bills, staff) => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        // Create staff lookup map for name resolution
        const staffMap = new Map();
        (staff || []).forEach(s => {
            if (s._id) staffMap.set(s._id, s);
            if (s.name) staffMap.set(s.name, s);
        });

        // Helper to get staff name
        const getStaffName = (staffRef) => {
            if (!staffRef) return null;
            if (typeof staffRef === 'string') {
                const staffDetails = staffMap.get(staffRef);
                return staffDetails?.name || 'Deleted Staff';
            }
            if (typeof staffRef === 'object') {
                if (staffRef.name) return staffRef.name;
                if (staffRef._id) {
                    const staffDetails = staffMap.get(staffRef._id);
                    return staffDetails?.name || 'Deleted Staff';
                }
            }
            return 'Unknown';
        };

        // Get date ranges for MoM and WoW comparison
        const thisMonthStart = new Date(currentYear, currentMonth, 1);
        const lastMonthStart = new Date(currentYear, currentMonth - 1, 1);
        const lastMonthEnd = new Date(currentYear, currentMonth, 0);

        const thisWeekStart = new Date(now);
        thisWeekStart.setDate(now.getDate() - now.getDay());
        thisWeekStart.setHours(0, 0, 0, 0);

        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeekStart);
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

        // Track unique clients and their visits
        const clientVisits = new Map();
        const monthlyClientVisits = {};
        const weeklyClientVisits = {};

        // Track bill-level data
        let totalBills = 0;
        let totalServicesCount = 0;
        let totalDiscountAmount = 0;
        let totalBaseAmount = 0;
        let totalRevenue = 0;
        let totalMembershipRevenue = 0;
        let membershipsCount = 0;

        // Staff daily data for utilization
        const staffDailyData = new Map();

        bills.forEach(bill => {
            const billDate = new Date(bill.timestamp || bill.createdAt);
            const clientId = bill.client?._id || bill.client?.phoneNumber;
            const monthKey = `${billDate.getFullYear()}-${String(billDate.getMonth() + 1).padStart(2, '0')}`;
            const weekKey = getWeekNumber(billDate);
            const dayKey = billDate.toISOString().split('T')[0];

            if (clientId) {
                // Track client visits
                if (!clientVisits.has(clientId)) {
                    clientVisits.set(clientId, []);
                }
                clientVisits.get(clientId).push(billDate);

                // Monthly client tracking
                if (!monthlyClientVisits[monthKey]) {
                    monthlyClientVisits[monthKey] = { total: new Set(), repeat: new Set(), allClients: new Map() };
                }
                const monthData = monthlyClientVisits[monthKey];
                if (monthData.allClients.has(clientId)) {
                    monthData.repeat.add(clientId);
                    monthData.allClients.set(clientId, monthData.allClients.get(clientId) + 1);
                } else {
                    monthData.allClients.set(clientId, 1);
                }
                monthData.total.add(clientId);

                // Weekly client tracking
                if (!weeklyClientVisits[weekKey]) {
                    weeklyClientVisits[weekKey] = { total: new Set(), repeat: new Set(), allClients: new Map() };
                }
                const weekData = weeklyClientVisits[weekKey];
                if (weekData.allClients.has(clientId)) {
                    weekData.repeat.add(clientId);
                    weekData.allClients.set(clientId, weekData.allClients.get(clientId) + 1);
                } else {
                    weekData.allClients.set(clientId, 1);
                }
                weekData.total.add(clientId);
            }

            totalBills++;
            const billRevenue = bill.calculations?.totals?.grandTotal || 0;
            totalRevenue += billRevenue;

            // Services count per bill
            const services = bill.services || [];
            totalServicesCount += services.length;

            // Discount tracking
            services.forEach(service => {
                const basePrice = service.pricing?.basePrice || 0;
                const finalPrice = service.pricing?.finalPrice || service.pricing?.totalPrice || 0;
                totalBaseAmount += basePrice;
                totalDiscountAmount += Math.max(0, basePrice - finalPrice);

                // Staff utilization tracking
                const staffRef = service.staff;
                if (staffRef) {
                    // Get staff ID - handle both string ID and object cases
                    const staffId = typeof staffRef === 'string' ? staffRef : (staffRef._id || staffRef.name);
                    const staffName = getStaffName(staffRef);

                    if (staffId && !staffDailyData.has(staffId)) {
                        staffDailyData.set(staffId, {
                            name: staffName,
                            dailyRevenue: {},
                            dailyServices: {},
                            totalRevenue: 0,
                            totalServices: 0
                        });
                    }
                    if (staffId) {
                        const staffData = staffDailyData.get(staffId);
                        staffData.dailyRevenue[dayKey] = (staffData.dailyRevenue[dayKey] || 0) + finalPrice;
                        staffData.dailyServices[dayKey] = (staffData.dailyServices[dayKey] || 0) + 1;
                        staffData.totalRevenue += finalPrice;
                        staffData.totalServices++;
                    }
                }
            });

            // Products discount
            const products = bill.products || [];
            products.forEach(product => {
                const basePrice = product.pricing?.basePrice || product.pricing?.mrp || 0;
                const finalPrice = product.pricing?.finalPrice || 0;
                totalBaseAmount += basePrice;
                totalDiscountAmount += Math.max(0, basePrice - finalPrice);
            });

            // Membership tracking - check both calculations and memberships array
            const membershipAmountFromCalc = bill.calculations?.items?.memberships?.finalTotal || 0;
            if (membershipAmountFromCalc > 0) {
                membershipsCount++;
                totalMembershipRevenue += membershipAmountFromCalc;
            } else {
                // Fallback to memberships array if exists
                const memberships = bill.memberships || [];
                memberships.forEach(membership => {
                    membershipsCount++;
                    totalMembershipRevenue += membership.pricing?.finalPrice || membership.price || 0;
                });
            }
        });

        // Calculate Repeat Visit Rate
        const totalClients = clientVisits.size;
        const repeatClients = Array.from(clientVisits.values()).filter(visits => visits.length > 1).length;
        const repeatVisitRate = totalClients > 0 ? (repeatClients / totalClients) * 100 : 0;

        // Calculate MoM comparison for repeat visit rate
        const currentMonthKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
        const lastMonthKey = `${currentMonth === 0 ? currentYear - 1 : currentYear}-${String(currentMonth === 0 ? 12 : currentMonth).padStart(2, '0')}`;

        const currentMonthData = monthlyClientVisits[currentMonthKey];
        const lastMonthData = monthlyClientVisits[lastMonthKey];

        const currentMonthRepeatRate = currentMonthData && currentMonthData.total.size > 0
            ? (currentMonthData.repeat.size / currentMonthData.total.size) * 100
            : 0;
        const lastMonthRepeatRate = lastMonthData && lastMonthData.total.size > 0
            ? (lastMonthData.repeat.size / lastMonthData.total.size) * 100
            : 0;
        const momRepeatChange = currentMonthRepeatRate - lastMonthRepeatRate;

        // Calculate WoW comparison
        const currentWeekKey = getWeekNumber(now);
        const lastWeekDate = new Date(now);
        lastWeekDate.setDate(lastWeekDate.getDate() - 7);
        const lastWeekKey = getWeekNumber(lastWeekDate);

        const currentWeekData = weeklyClientVisits[currentWeekKey];
        const lastWeekData = weeklyClientVisits[lastWeekKey];

        const currentWeekRepeatRate = currentWeekData && currentWeekData.total.size > 0
            ? (currentWeekData.repeat.size / currentWeekData.total.size) * 100
            : 0;
        const lastWeekRepeatRate = lastWeekData && lastWeekData.total.size > 0
            ? (lastWeekData.repeat.size / lastWeekData.total.size) * 100
            : 0;
        const wowRepeatChange = currentWeekRepeatRate - lastWeekRepeatRate;

        // Calculate Avg Days Between Visits (for repeat customers only)
        let totalDaysBetweenVisits = 0;
        let visitGapsCount = 0;

        clientVisits.forEach((visits, clientId) => {
            if (visits.length > 1) {
                const sortedVisits = [...visits].sort((a, b) => new Date(a) - new Date(b));
                for (let i = 1; i < sortedVisits.length; i++) {
                    const daysBetween = Math.floor((new Date(sortedVisits[i]) - new Date(sortedVisits[i - 1])) / (1000 * 60 * 60 * 24));
                    totalDaysBetweenVisits += daysBetween;
                    visitGapsCount++;
                }
            }
        });

        const avgDaysBetweenVisits = visitGapsCount > 0 ? Math.round(totalDaysBetweenVisits / visitGapsCount) : 0;

        // Calculate ARPU (Average Revenue Per User)
        const arpu = totalClients > 0 ? totalRevenue / totalClients : 0;

        // Calculate Services per Bill
        const servicesPerBill = totalBills > 0 ? totalServicesCount / totalBills : 0;

        // Calculate Discount Intensity
        const discountIntensity = totalBaseAmount > 0 ? (totalDiscountAmount / totalBaseAmount) * 100 : 0;

        // Calculate Membership Penetration
        const membershipPenetration = totalBills > 0 ? (membershipsCount / totalBills) * 100 : 0;

        // Calculate Staff Utilization & Revenue per Stylist per Day
        const staffMetrics = Array.from(staffDailyData.values()).map(staffData => {
            const workingDays = Object.keys(staffData.dailyRevenue).length;
            const avgRevenuePerDay = workingDays > 0 ? staffData.totalRevenue / workingDays : 0;
            const avgServicesPerDay = workingDays > 0 ? staffData.totalServices / workingDays : 0;

            // Assuming 8 hours per day, and average 1 hour per service
            const availableHoursPerDay = 8;
            const bookedHoursPerDay = avgServicesPerDay; // Simplified: 1 service = 1 hour
            const utilizationRate = (bookedHoursPerDay / availableHoursPerDay) * 100;

            return {
                name: staffData.name,
                workingDays,
                totalRevenue: staffData.totalRevenue,
                totalServices: staffData.totalServices,
                avgRevenuePerDay,
                avgServicesPerDay,
                utilizationRate: Math.min(utilizationRate, 100)
            };
        }).sort((a, b) => b.avgRevenuePerDay - a.avgRevenuePerDay);

        // Calculate team averages
        const avgRevenuePerStylistPerDay = staffMetrics.length > 0
            ? staffMetrics.reduce((sum, s) => sum + s.avgRevenuePerDay, 0) / staffMetrics.length
            : 0;
        const avgUtilizationRate = staffMetrics.length > 0
            ? staffMetrics.reduce((sum, s) => sum + s.utilizationRate, 0) / staffMetrics.length
            : 0;

        // Monthly trend data for repeat visit rate
        const sortedMonths = Object.keys(monthlyClientVisits).sort().slice(-6);
        const repeatRateTrend = sortedMonths.map(month => {
            const data = monthlyClientVisits[month];
            const rate = data && data.total.size > 0 ? (data.repeat.size / data.total.size) * 100 : 0;
            return {
                month: formatMonthLabel(month),
                repeatRate: rate,
                totalClients: data?.total.size || 0,
                repeatClients: data?.repeat.size || 0
            };
        });

        setKeyMetrics({
            // Repeat Visit Rate
            repeatVisitRate,
            repeatClients,
            totalClients,
            currentMonthRepeatRate,
            lastMonthRepeatRate,
            momRepeatChange,
            currentWeekRepeatRate,
            lastWeekRepeatRate,
            wowRepeatChange,
            repeatRateTrend,

            // Avg Days Between Visits
            avgDaysBetweenVisits,
            repeatCustomersCount: repeatClients,

            // ARPU
            arpu,
            totalRevenue,

            // Services per Bill
            servicesPerBill,
            totalServicesCount,
            totalBills,

            // Discount Intensity
            discountIntensity,
            totalDiscountAmount,
            totalBaseAmount,

            // Membership
            membershipPenetration,
            membershipsCount,
            totalMembershipRevenue,

            // Staff Utilization
            avgUtilizationRate,
            avgRevenuePerStylistPerDay,
            staffMetrics
        });
    };

    // ============= HELPER FUNCTIONS =============
    const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return `${d.getUTCFullYear()}-W${Math.ceil((((d - yearStart) / 86400000) + 1) / 7)}`;
    };

    const formatMonthLabel = (monthKey) => {
        const [year, month] = monthKey.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`;
    };

    const calculateGrowthRate = (values) => {
        if (values.length < 2) return 0;
        const validValues = values.filter(v => v > 0);
        if (validValues.length < 2) return 0;

        let totalGrowth = 0;
        let count = 0;
        for (let i = 1; i < validValues.length; i++) {
            if (validValues[i - 1] > 0) {
                totalGrowth += (validValues[i] - validValues[i - 1]) / validValues[i - 1];
                count++;
            }
        }
        return count > 0 ? totalGrowth / count : 0;
    };

    const calculateAverageVisitGap = (visitDates) => {
        if (visitDates.length < 2) return null;
        const sortedDates = [...visitDates].sort((a, b) => new Date(a) - new Date(b));
        let totalGap = 0;
        for (let i = 1; i < sortedDates.length; i++) {
            totalGap += (new Date(sortedDates[i]) - new Date(sortedDates[i - 1])) / (1000 * 60 * 60 * 24);
        }
        return Math.round(totalGap / (sortedDates.length - 1));
    };

    const calculatePaymentMethodBreakdown = (bills) => {
        const methods = { Cash: 0, UPI: 0, Card: 0, Wallet: 0, Other: 0 };
        bills.forEach(bill => {
            const paymentMethods = bill.payment?.methods || {};
            methods.Cash += paymentMethods.cash || 0;
            methods.UPI += paymentMethods.upi || 0;
            methods.Card += paymentMethods.card || 0;
            methods.Wallet += paymentMethods.wallet || 0;
            methods.Other += paymentMethods.other || 0;
        });
        return Object.entries(methods).filter(([_, v]) => v > 0).map(([name, value]) => ({ name, value }));
    };

    const calculateRevenueByCategory = (bills) => {
        let services = 0, products = 0, memberships = 0;
        bills.forEach(bill => {
            services += bill.calculations?.items?.services?.finalTotal || 0;
            products += bill.calculations?.items?.products?.finalTotal || 0;
            memberships += bill.calculations?.items?.memberships?.finalTotal || 0;
        });
        return [
            { name: 'Services', value: services },
            { name: 'Products', value: products },
            { name: 'Memberships', value: memberships }
        ].filter(c => c.value > 0);
    };

    const categorizeService = (serviceName) => {
        const name = (serviceName || '').toLowerCase();
        if (name.includes('hair') || name.includes('cut') || name.includes('color') || name.includes('style')) return 'Hair';
        if (name.includes('facial') || name.includes('skin') || name.includes('face')) return 'Facial';
        if (name.includes('spa') || name.includes('massage') || name.includes('body')) return 'Spa';
        if (name.includes('nail') || name.includes('mani') || name.includes('pedi')) return 'Nails';
        if (name.includes('makeup') || name.includes('bridal')) return 'Makeup';
        return 'Other';
    };

    const formatCurrency = (amount) => {
        return `₹${Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    };

    const COLORS = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#F97316'];

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 rounded-xl shadow-xl border border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="text-sm" style={{ color: entry.color }}>
                            {entry.name}: {entry.name.toLowerCase().includes('revenue') || entry.name.toLowerCase().includes('income') || entry.name.toLowerCase().includes('expense') || entry.name.toLowerCase().includes('flow')
                                ? formatCurrency(entry.value)
                                : entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="p-5">
                <PageHeader title="Business Forecasting" description="AI-Powered Business Intelligence" />
                <div className="flex justify-center items-center h-64">
                    <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
                        <p className="text-gray-500 animate-pulse">Analyzing business data...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='p-5 bg-gray-50 min-h-screen'>
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-6 mb-6 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/20 p-3 rounded-xl">
                            <Brain className="w-8 h-8" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Business Forecasting</h1>
                            <p className="text-white/80 text-sm">AI-Powered Predictive Analytics & Insights</p>
                        </div>
                    </div>
                    <button
                        onClick={fetchAllData}
                        className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-all"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl p-2 mb-6 flex flex-wrap gap-2 shadow-sm">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === tab.id
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Forecasted Revenue"
                            value={formatCurrency(revenueForecast?.forecastedNextMonth || 0)}
                            subtitle="Next Month"
                            icon={TrendingUp}
                            trend={revenueForecast?.growthRate}
                            color="indigo"
                        />
                        <MetricCard
                            title="Client Retention"
                            value={`${(clientForecast?.retentionRate || 0).toFixed(1)}%`}
                            subtitle="Rate"
                            icon={Users}
                            trend={null}
                            color="emerald"
                        />
                        <MetricCard
                            title="Risk Score"
                            value={`${riskAnalysis?.riskScore || 0}%`}
                            subtitle={riskAnalysis?.overallRiskLevel || 'Low'}
                            icon={ShieldAlert}
                            trend={null}
                            color={riskAnalysis?.overallRiskLevel === 'High' ? 'rose' : riskAnalysis?.overallRiskLevel === 'Medium' ? 'amber' : 'emerald'}
                        />
                        <MetricCard
                            title="Cash Flow"
                            value={formatCurrency(cashFlowForecast?.avgMonthlyNetFlow || 0)}
                            subtitle="Monthly Avg"
                            icon={Wallet}
                            trend={cashFlowForecast?.incomeGrowthRate}
                            color="violet"
                        />
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Revenue Trend */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-indigo-600" />
                                Revenue Forecast
                            </h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <AreaChart data={revenueForecast?.monthlyTrend || []}>
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke="#6366F1"
                                        strokeWidth={2}
                                        fill="url(#colorRevenue)"
                                        strokeDasharray={(d) => d.isForecast ? "5 5" : "0"}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Client Segments */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Users className="w-5 h-5 text-emerald-600" />
                                Client Segments
                            </h3>
                            <div className="flex items-center">
                                <ResponsiveContainer width="50%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={[
                                                { name: 'Loyal', value: clientForecast?.segments?.loyal || 0 },
                                                { name: 'Regular', value: clientForecast?.segments?.regular || 0 },
                                                { name: 'Occasional', value: clientForecast?.segments?.occasional || 0 },
                                                { name: 'One-time', value: clientForecast?.segments?.oneTime || 0 }
                                            ]}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={40}
                                            outerRadius={80}
                                            paddingAngle={3}
                                            dataKey="value"
                                        >
                                            {COLORS.map((color, index) => (
                                                <Cell key={`cell-${index}`} fill={color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="flex-1 space-y-2">
                                    {[
                                        { name: 'Loyal (10+ visits)', value: clientForecast?.segments?.loyal || 0, color: '#6366F1' },
                                        { name: 'Regular (5-9 visits)', value: clientForecast?.segments?.regular || 0, color: '#EC4899' },
                                        { name: 'Occasional (2-4 visits)', value: clientForecast?.segments?.occasional || 0, color: '#10B981' },
                                        { name: 'One-time (1 visit)', value: clientForecast?.segments?.oneTime || 0, color: '#F59E0B' }
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                            <span className="text-sm text-gray-600">{item.name}</span>
                                            <span className="text-sm font-bold text-gray-900 ml-auto">{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Risk Overview */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 text-rose-600" />
                            Risk Overview
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {riskAnalysis?.risks?.slice(0, 4).map((risk, index) => (
                                <div
                                    key={index}
                                    className={`p-4 rounded-xl border-l-4 ${risk.severity === 'High' ? 'bg-rose-50 border-rose-500' :
                                        risk.severity === 'Medium' ? 'bg-amber-50 border-amber-500' :
                                            'bg-emerald-50 border-emerald-500'
                                        }`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${risk.severity === 'High' ? 'bg-rose-200 text-rose-700' :
                                            risk.severity === 'Medium' ? 'bg-amber-200 text-amber-700' :
                                                'bg-emerald-200 text-emerald-700'
                                            }`}>
                                            {risk.severity}
                                        </span>
                                    </div>
                                    <h4 className="font-semibold text-gray-900">{risk.type}</h4>
                                    <p className="text-xs text-gray-600 mt-1">{risk.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Key Metrics Tab */}
            {activeTab === 'keymetrics' && (
                <div className="space-y-6">
                    {/* Primary KPIs Row 1 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Repeat Visit Rate"
                            value={`${(keyMetrics?.repeatVisitRate || 0).toFixed(1)}%`}
                            subtitle={`${keyMetrics?.repeatClients || 0} of ${keyMetrics?.totalClients || 0} clients`}
                            icon={RefreshCw}
                            color="indigo"
                        />
                        <MetricCard
                            title="Avg Days Between Visits"
                            value={`${keyMetrics?.avgDaysBetweenVisits || 0} days`}
                            subtitle={`${keyMetrics?.repeatCustomersCount || 0} repeat customers`}
                            icon={CalendarDays}
                            color="violet"
                        />
                        <MetricCard
                            title="ARPU"
                            value={formatCurrency(keyMetrics?.arpu || 0)}
                            subtitle="Avg Revenue Per User"
                            icon={UserCheck}
                            color="emerald"
                        />
                        <MetricCard
                            title="Services per Bill"
                            value={(keyMetrics?.servicesPerBill || 0).toFixed(2)}
                            subtitle={`${keyMetrics?.totalServicesCount || 0} services, ${keyMetrics?.totalBills || 0} bills`}
                            icon={Scissors}
                            color="amber"
                        />
                    </div>

                    {/* Primary KPIs Row 2 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Discount Intensity"
                            value={`${(keyMetrics?.discountIntensity || 0).toFixed(1)}%`}
                            subtitle={`${formatCurrency(keyMetrics?.totalDiscountAmount || 0)} discounted`}
                            icon={Percent}
                            color="rose"
                        />
                        <MetricCard
                            title="Membership Penetration"
                            value={`${(keyMetrics?.membershipPenetration || 0).toFixed(1)}%`}
                            subtitle={`${keyMetrics?.membershipsCount || 0} memberships`}
                            icon={Award}
                            color="indigo"
                        />
                        <MetricCard
                            title="Staff Utilization"
                            value={`${(keyMetrics?.avgUtilizationRate || 0).toFixed(1)}%`}
                            subtitle="Booked / Available Hours"
                            icon={Timer}
                            color="violet"
                        />
                        <MetricCard
                            title="Revenue/Stylist/Day"
                            value={formatCurrency(keyMetrics?.avgRevenuePerStylistPerDay || 0)}
                            subtitle="Team average"
                            icon={Users2}
                            color="emerald"
                        />
                    </div>

                    {/* Repeat Visit Rate Comparison */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <RefreshCw className="w-5 h-5 text-indigo-600" />
                                Repeat Visit Rate Comparison
                            </h3>
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                {/* MoM Comparison */}
                                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                                    <p className="text-sm text-gray-600 mb-1">Month over Month</p>
                                    <div className="flex items-center gap-2">
                                        <p className="text-2xl font-bold text-indigo-700">
                                            {(keyMetrics?.currentMonthRepeatRate || 0).toFixed(1)}%
                                        </p>
                                        <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${(keyMetrics?.momRepeatChange || 0) >= 0
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-rose-100 text-rose-700'
                                            }`}>
                                            {(keyMetrics?.momRepeatChange || 0) >= 0 ? '+' : ''}{(keyMetrics?.momRepeatChange || 0).toFixed(1)}%
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        vs {(keyMetrics?.lastMonthRepeatRate || 0).toFixed(1)}% last month
                                    </p>
                                </div>
                                {/* WoW Comparison */}
                                <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-xl p-4 border border-violet-100">
                                    <p className="text-sm text-gray-600 mb-1">Week over Week</p>
                                    <div className="flex items-center gap-2">
                                        <p className="text-2xl font-bold text-violet-700">
                                            {(keyMetrics?.currentWeekRepeatRate || 0).toFixed(1)}%
                                        </p>
                                        <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${(keyMetrics?.wowRepeatChange || 0) >= 0
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-rose-100 text-rose-700'
                                            }`}>
                                            {(keyMetrics?.wowRepeatChange || 0) >= 0 ? '+' : ''}{(keyMetrics?.wowRepeatChange || 0).toFixed(1)}%
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        vs {(keyMetrics?.lastWeekRepeatRate || 0).toFixed(1)}% last week
                                    </p>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={200}>
                                <AreaChart data={keyMetrics?.repeatRateTrend || []}>
                                    <defs>
                                        <linearGradient id="colorRepeatRate" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <YAxis tickFormatter={(v) => `${v.toFixed(0)}%`} tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                                    <Area type="monotone" dataKey="repeatRate" name="Repeat Rate" stroke="#6366F1" fill="url(#colorRepeatRate)" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* ARPU & Services Analysis */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <BarChart2 className="w-5 h-5 text-emerald-600" />
                                Revenue & Service Metrics
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <CircleDollarSign className="w-8 h-8 text-emerald-600" />
                                        <div>
                                            <p className="font-medium text-gray-900">ARPU</p>
                                            <p className="text-xs text-gray-500">Average Revenue Per User</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(keyMetrics?.arpu || 0)}</p>
                                        <p className="text-xs text-gray-500">{keyMetrics?.totalClients || 0} users</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <Scissors className="w-8 h-8 text-amber-600" />
                                        <div>
                                            <p className="font-medium text-gray-900">Services per Bill</p>
                                            <p className="text-xs text-gray-500">Average services per transaction</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-amber-600">{(keyMetrics?.servicesPerBill || 0).toFixed(2)}</p>
                                        <p className="text-xs text-gray-500">{keyMetrics?.totalServicesCount || 0} total services</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-rose-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <Percent className="w-8 h-8 text-rose-600" />
                                        <div>
                                            <p className="font-medium text-gray-900">Discount Intensity</p>
                                            <p className="text-xs text-gray-500">Percentage of revenue discounted</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-rose-600">{(keyMetrics?.discountIntensity || 0).toFixed(1)}%</p>
                                        <p className="text-xs text-gray-500">{formatCurrency(keyMetrics?.totalDiscountAmount || 0)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Membership Analysis */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white">
                            <Award className="w-10 h-10 mb-3 opacity-80" />
                            <p className="text-sm opacity-80">Membership Penetration</p>
                            <p className="text-4xl font-bold">{(keyMetrics?.membershipPenetration || 0).toFixed(1)}%</p>
                            <p className="text-sm mt-2 opacity-80">
                                {keyMetrics?.membershipsCount || 0} memberships sold
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
                            <Banknote className="w-10 h-10 mb-3 opacity-80" />
                            <p className="text-sm opacity-80">Membership Revenue</p>
                            <p className="text-4xl font-bold">{formatCurrency(keyMetrics?.totalMembershipRevenue || 0)}</p>
                            <p className="text-sm mt-2 opacity-80">
                                Total from memberships
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white">
                            <CalendarDays className="w-10 h-10 mb-3 opacity-80" />
                            <p className="text-sm opacity-80">Avg Days Between Visits</p>
                            <p className="text-4xl font-bold">{keyMetrics?.avgDaysBetweenVisits || 0} days</p>
                            <p className="text-sm mt-2 opacity-80">
                                For repeat customers only
                            </p>
                        </div>
                    </div>

                    {/* Staff Utilization Table */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Timer className="w-5 h-5 text-violet-600" />
                            Staff Utilization & Revenue per Day
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b bg-gray-50">
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Stylist</th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Working Days</th>
                                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Total Revenue</th>
                                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Revenue/Day</th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Services/Day</th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Utilization</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {keyMetrics?.staffMetrics?.map((staff, index) => (
                                        <tr key={index} className="border-b hover:bg-gray-50">
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
                                                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                                                            index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500' :
                                                                'bg-gradient-to-br from-indigo-400 to-indigo-500'
                                                        }`}>
                                                        {index + 1}
                                                    </div>
                                                    <p className="font-medium text-gray-900">{staff.name}</p>
                                                </div>
                                            </td>
                                            <td className="text-center py-3 px-4">
                                                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-sm">
                                                    {staff.workingDays}
                                                </span>
                                            </td>
                                            <td className="text-right py-3 px-4 font-semibold text-gray-900">
                                                {formatCurrency(staff.totalRevenue)}
                                            </td>
                                            <td className="text-right py-3 px-4">
                                                <span className="font-bold text-emerald-600">{formatCurrency(staff.avgRevenuePerDay)}</span>
                                            </td>
                                            <td className="text-center py-3 px-4">
                                                <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full text-sm font-semibold">
                                                    {staff.avgServicesPerDay.toFixed(1)}
                                                </span>
                                            </td>
                                            <td className="text-center py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${staff.utilizationRate >= 80 ? 'bg-emerald-500' :
                                                                staff.utilizationRate >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                                                                }`}
                                                            style={{ width: `${Math.min(staff.utilizationRate, 100)}%` }}
                                                        />
                                                    </div>
                                                    <span className={`text-sm font-semibold ${staff.utilizationRate >= 80 ? 'text-emerald-600' :
                                                        staff.utilizationRate >= 50 ? 'text-amber-600' : 'text-rose-600'
                                                        }`}>
                                                        {staff.utilizationRate.toFixed(0)}%
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {(!keyMetrics?.staffMetrics || keyMetrics.staffMetrics.length === 0) && (
                            <div className="text-center py-8 text-gray-500">
                                <Users2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>No staff data available</p>
                            </div>
                        )}
                    </div>

                    {/* Quick Insights */}
                    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-6 text-white">
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                            <Brain className="w-5 h-5" />
                            Quick Insights
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                                <p className="text-sm opacity-80 mb-1">Customer Loyalty</p>
                                <p className="text-xl font-bold">
                                    {(keyMetrics?.repeatVisitRate || 0) >= 40 ? 'Strong' :
                                        (keyMetrics?.repeatVisitRate || 0) >= 20 ? 'Moderate' : 'Needs Work'}
                                </p>
                                <p className="text-xs opacity-70 mt-1">
                                    {(keyMetrics?.repeatVisitRate || 0).toFixed(1)}% repeat rate
                                </p>
                            </div>
                            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                                <p className="text-sm opacity-80 mb-1">Service Bundling</p>
                                <p className="text-xl font-bold">
                                    {(keyMetrics?.servicesPerBill || 0) >= 2 ? 'Excellent' :
                                        (keyMetrics?.servicesPerBill || 0) >= 1.5 ? 'Good' : 'Room to Grow'}
                                </p>
                                <p className="text-xs opacity-70 mt-1">
                                    {(keyMetrics?.servicesPerBill || 0).toFixed(2)} services/bill
                                </p>
                            </div>
                            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                                <p className="text-sm opacity-80 mb-1">Discount Strategy</p>
                                <p className="text-xl font-bold">
                                    {(keyMetrics?.discountIntensity || 0) <= 10 ? 'Conservative' :
                                        (keyMetrics?.discountIntensity || 0) <= 20 ? 'Balanced' : 'Aggressive'}
                                </p>
                                <p className="text-xs opacity-70 mt-1">
                                    {(keyMetrics?.discountIntensity || 0).toFixed(1)}% discount rate
                                </p>
                            </div>
                            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                                <p className="text-sm opacity-80 mb-1">Team Efficiency</p>
                                <p className="text-xl font-bold">
                                    {(keyMetrics?.avgUtilizationRate || 0) >= 75 ? 'Optimal' :
                                        (keyMetrics?.avgUtilizationRate || 0) >= 50 ? 'Good' : 'Underutilized'}
                                </p>
                                <p className="text-xs opacity-70 mt-1">
                                    {(keyMetrics?.avgUtilizationRate || 0).toFixed(1)}% utilized
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Revenue Tab */}
            {activeTab === 'revenue' && (
                <div className="space-y-6">
                    {/* Revenue KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Total Revenue"
                            value={formatCurrency(revenueForecast?.totalRevenue || 0)}
                            subtitle="All Time"
                            icon={IndianRupee}
                            color="indigo"
                        />
                        <MetricCard
                            title="This Month"
                            value={formatCurrency(revenueForecast?.currentMonthRevenue || 0)}
                            subtitle={`vs ${formatCurrency(revenueForecast?.previousMonthRevenue || 0)} last`}
                            icon={Calendar}
                            trend={revenueForecast?.monthOverMonthGrowth}
                            color="emerald"
                        />
                        <MetricCard
                            title="Forecasted"
                            value={formatCurrency(revenueForecast?.forecastedNextMonth || 0)}
                            subtitle="Next Month"
                            icon={Target}
                            color="violet"
                        />
                        <MetricCard
                            title="Profit Margin"
                            value={`${(revenueForecast?.profitMargin || 0).toFixed(1)}%`}
                            subtitle={formatCurrency(revenueForecast?.netProfit || 0)}
                            icon={Percent}
                            color="amber"
                        />
                    </div>

                    {/* Revenue Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Trend & Forecast</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <ComposedChart data={revenueForecast?.monthlyTrend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="revenue" fill="#6366F1" radius={[4, 4, 0, 0]} />
                                    <Line type="monotone" dataKey="revenue" stroke="#EC4899" strokeWidth={2} dot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue by Category</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={revenueForecast?.revenueByCategory || []}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={3}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {(revenueForecast?.revenueByCategory || []).map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Payment Methods */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Method Distribution</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={revenueForecast?.revenueByPaymentMethod || []} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                                <YAxis dataKey="name" type="category" tick={{ fill: '#6b7280', fontSize: 12 }} width={80} />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Bar dataKey="value" fill="#6366F1" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Clients Tab */}
            {activeTab === 'clients' && (
                <div className="space-y-6">
                    {/* Client KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Total Clients"
                            value={clientForecast?.totalClients || 0}
                            subtitle="Unique customers"
                            icon={Users}
                            color="indigo"
                        />
                        <MetricCard
                            title="Avg Lifetime Value"
                            value={formatCurrency(clientForecast?.avgLTV || 0)}
                            subtitle="Per client"
                            icon={DollarSign}
                            color="emerald"
                        />
                        <MetricCard
                            title="Churn Risk"
                            value={clientForecast?.churnAnalysis?.highRisk || 0}
                            subtitle="High risk clients"
                            icon={UserMinus}
                            color="rose"
                        />
                        <MetricCard
                            title="New Clients/Month"
                            value={(clientForecast?.avgNewClientsPerMonth || 0).toFixed(0)}
                            subtitle={`${(clientForecast?.newClientGrowthRate || 0).toFixed(1)}% growth`}
                            icon={UserPlus}
                            trend={clientForecast?.newClientGrowthRate}
                            color="violet"
                        />
                    </div>

                    {/* Churn Analysis */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-600" />
                                Churn Risk Distribution
                            </h3>
                            <div className="grid grid-cols-3 gap-4 mb-6">
                                <div className="bg-rose-50 rounded-xl p-4 text-center">
                                    <p className="text-3xl font-bold text-rose-600">{clientForecast?.churnAnalysis?.highRisk || 0}</p>
                                    <p className="text-sm text-rose-700">High Risk</p>
                                </div>
                                <div className="bg-amber-50 rounded-xl p-4 text-center">
                                    <p className="text-3xl font-bold text-amber-600">{clientForecast?.churnAnalysis?.mediumRisk || 0}</p>
                                    <p className="text-sm text-amber-700">Medium Risk</p>
                                </div>
                                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                                    <p className="text-3xl font-bold text-emerald-600">{clientForecast?.churnAnalysis?.lowRisk || 0}</p>
                                    <p className="text-sm text-emerald-700">Low Risk</p>
                                </div>
                            </div>
                            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                                <p className="text-sm font-semibold text-rose-800">Potential Revenue Loss</p>
                                <p className="text-2xl font-bold text-rose-600">{formatCurrency(clientForecast?.churnAnalysis?.potentialRevenueLoss || 0)}</p>
                                <p className="text-xs text-rose-600">If high-risk clients churn</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Client Acquisition Trend</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={clientForecast?.clientTrend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="newClients" name="New Clients" fill="#6366F1" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="returningClients" name="Returning" fill="#10B981" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* High Risk Clients List */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">High Risk Clients - Immediate Attention Required</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Client</th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Visits</th>
                                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Total Spent</th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Last Visit</th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Churn Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientForecast?.churnAnalysis?.highRiskClients?.map((client, index) => (
                                        <tr key={index} className="border-b hover:bg-gray-50">
                                            <td className="py-3 px-4">
                                                <p className="font-medium text-gray-900">{client.name}</p>
                                            </td>
                                            <td className="text-center py-3 px-4">
                                                <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full text-sm font-semibold">
                                                    {client.totalVisits}
                                                </span>
                                            </td>
                                            <td className="text-right py-3 px-4 font-semibold text-emerald-600">
                                                {formatCurrency(client.totalSpent)}
                                            </td>
                                            <td className="text-center py-3 px-4">
                                                <span className="text-rose-600 font-medium">{client.daysSinceLastVisit} days ago</span>
                                            </td>
                                            <td className="text-center py-3 px-4">
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-rose-500 h-2 rounded-full"
                                                        style={{ width: `${client.churnScore}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs text-gray-500">{client.churnScore}%</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Staff Tab */}
            {activeTab === 'staff' && (
                <div className="space-y-6">
                    {/* Staff KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Team Revenue"
                            value={formatCurrency(staffForecast?.totalTeamRevenue || 0)}
                            subtitle="Total generated"
                            icon={IndianRupee}
                            color="indigo"
                        />
                        <MetricCard
                            title="Avg Per Staff"
                            value={formatCurrency(staffForecast?.avgRevenuePerStaff || 0)}
                            subtitle="Revenue"
                            icon={UserCheck}
                            color="emerald"
                        />
                        <MetricCard
                            title="Current Staff"
                            value={staffForecast?.currentStaffCount || 0}
                            subtitle={`Optimal: ${staffForecast?.optimalStaffCount || 0}`}
                            icon={Users2}
                            color="violet"
                        />
                        <MetricCard
                            title="Services Done"
                            value={staffForecast?.totalServices || 0}
                            subtitle={`Products: ${staffForecast?.totalProducts || 0}`}
                            icon={Scissors}
                            color="amber"
                        />
                    </div>

                    {/* Staff Performance */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Award className="w-5 h-5 text-amber-500" />
                                Top Performers
                            </h3>
                            <div className="space-y-3">
                                {staffForecast?.topPerformers?.map((staff, index) => (
                                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
                                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                                                index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500' :
                                                    'bg-gradient-to-br from-indigo-400 to-indigo-500'
                                            }`}>
                                            {index + 1}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-900">{staff.name}</p>
                                            <p className="text-xs text-gray-500">{staff.servicesCount} services, {staff.productsCount} products</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-emerald-600">{formatCurrency(staff.totalRevenue)}</p>
                                            <p className={`text-xs ${staff.weeklyTrend >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                                {staff.weeklyTrend >= 0 ? '+' : ''}{staff.weeklyTrend.toFixed(1)}% this week
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Performance Distribution</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={staffForecast?.performanceDistribution || []}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={90}
                                        paddingAngle={3}
                                        dataKey="value"
                                        label={({ name, value }) => `${name}: ${value}`}
                                    >
                                        <Cell fill="#10B981" />
                                        <Cell fill="#6366F1" />
                                        <Cell fill="#F59E0B" />
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Staffing Recommendation */}
                    <div className={`rounded-2xl p-6 ${staffForecast?.staffingRecommendation === 'Hire more staff' ? 'bg-amber-50 border-2 border-amber-200' :
                        staffForecast?.staffingRecommendation === 'Consider reducing staff' ? 'bg-rose-50 border-2 border-rose-200' :
                            'bg-emerald-50 border-2 border-emerald-200'
                        }`}>
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${staffForecast?.staffingRecommendation === 'Hire more staff' ? 'bg-amber-200' :
                                staffForecast?.staffingRecommendation === 'Consider reducing staff' ? 'bg-rose-200' :
                                    'bg-emerald-200'
                                }`}>
                                <Users2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Staffing Recommendation</h3>
                                <p className="text-gray-600">{staffForecast?.staffingRecommendation}</p>
                                <p className="text-sm text-gray-500 mt-1">
                                    Current: {staffForecast?.currentStaffCount} | Optimal: {staffForecast?.optimalStaffCount}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Inventory Tab */}
            {activeTab === 'inventory' && (
                <div className="space-y-6">
                    {/* Inventory KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Total Products"
                            value={inventoryForecast?.totalProducts || 0}
                            subtitle="In catalog"
                            icon={Package}
                            color="indigo"
                        />
                        <MetricCard
                            title="Inventory Value"
                            value={formatCurrency(inventoryForecast?.totalInventoryValue || 0)}
                            subtitle="Current stock"
                            icon={DollarSign}
                            color="emerald"
                        />
                        <MetricCard
                            title="Low Stock"
                            value={inventoryForecast?.lowStockCount || 0}
                            subtitle="Need reorder"
                            icon={AlertCircle}
                            color="amber"
                        />
                        <MetricCard
                            title="Out of Stock"
                            value={inventoryForecast?.outOfStockCount || 0}
                            subtitle="Critical"
                            icon={AlertTriangle}
                            color="rose"
                        />
                    </div>

                    {/* Top Products & Reorder */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-emerald-600" />
                                Top Selling Products
                            </h3>
                            <div className="space-y-3">
                                {inventoryForecast?.topProducts?.slice(0, 5).map((product, index) => (
                                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                                            {index + 1}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900">{product.name}</p>
                                            <p className="text-xs text-gray-500">{product.avgMonthlySales?.toFixed(0)} sold/month</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-emerald-600">{formatCurrency(product.totalRevenue)}</p>
                                            <p className={`text-xs ${product.growthRate >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                                {product.growthRate >= 0 ? '+' : ''}{product.growthRate?.toFixed(1)}%
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-amber-600" />
                                Reorder Recommendations
                            </h3>
                            {inventoryForecast?.reorderRecommendations?.length > 0 ? (
                                <div className="space-y-3">
                                    {inventoryForecast.reorderRecommendations.slice(0, 5).map((item, index) => (
                                        <div key={index} className={`p-3 rounded-xl border-l-4 ${item.urgency === 'Critical' ? 'bg-rose-50 border-rose-500' : 'bg-amber-50 border-amber-500'
                                            }`}>
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-medium text-gray-900">{item.product}</p>
                                                    <p className="text-xs text-gray-500">Current: {item.currentStock}</p>
                                                </div>
                                                <div className="text-right">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${item.urgency === 'Critical' ? 'bg-rose-200 text-rose-700' : 'bg-amber-200 text-amber-700'
                                                        }`}>
                                                        {item.urgency}
                                                    </span>
                                                    <p className="text-sm font-semibold text-gray-900 mt-1">Order: {item.recommended}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                                    <p>All products are well-stocked!</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Product Movement Analysis */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Product Usage Trend</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={inventoryForecast?.inventoryTrend || []}>
                                <defs>
                                    <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                                <Tooltip />
                                <Area type="monotone" dataKey="usage" stroke="#6366F1" fill="url(#colorUsage)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
                <div className="space-y-6">
                    {/* Service KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Total Services"
                            value={serviceForecast?.totalServices || 0}
                            subtitle="Available"
                            icon={Scissors}
                            color="indigo"
                        />
                        <MetricCard
                            title="Service Revenue"
                            value={formatCurrency(serviceForecast?.totalServiceRevenue || 0)}
                            subtitle="Total earned"
                            icon={IndianRupee}
                            color="emerald"
                        />
                        <MetricCard
                            title="Services Rendered"
                            value={serviceForecast?.totalServicesRendered || 0}
                            subtitle={`${serviceForecast?.totalServices || 0} unique services`}
                            icon={TrendingUp}
                            color="violet"
                        />
                        <MetricCard
                            title="Avg Service Price"
                            value={formatCurrency(serviceForecast?.avgServicePrice || 0)}
                            subtitle="Per service"
                            icon={DollarSign}
                            color="amber"
                        />
                    </div>

                    {/* Service Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Top Services by Revenue</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={serviceForecast?.topServices?.slice(0, 7) || []} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis type="number" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                    <YAxis dataKey="name" type="category" width={120} tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <Tooltip formatter={(v) => formatCurrency(v)} />
                                    <Bar dataKey="totalRevenue" fill="#6366F1" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue by Service</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={serviceForecast?.categoryBreakdown || []}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={100}
                                        paddingAngle={3}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {(serviceForecast?.categoryBreakdown || []).map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v) => formatCurrency(v)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Service Usage Breakdown - Most Popular Services */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Most Popular Services (by Usage)</h3>
                        <div className="space-y-3">
                            {serviceForecast?.serviceUsageBreakdown?.map((service, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}>
                                        {index + 1}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="font-medium text-gray-900">{service.name}</span>
                                            <span className="text-sm font-bold" style={{ color: COLORS[index % COLORS.length] }}>
                                                {service.percentage}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-2">
                                            <div
                                                className="h-2 rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${Math.min(service.percentage, 100)}%`,
                                                    backgroundColor: COLORS[index % COLORS.length]
                                                }}
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">{service.count} times used</p>
                                    </div>
                                </div>
                            ))}
                            {(!serviceForecast?.serviceUsageBreakdown || serviceForecast.serviceUsageBreakdown.length === 0) && (
                                <p className="text-gray-500 text-center py-4">No service usage data available</p>
                            )}
                        </div>
                    </div>

                    {/* Service Recommendations */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
                            <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5" />
                                Promote These
                            </h4>
                            <ul className="space-y-2">
                                {serviceForecast?.recommendations?.promote?.map((service, i) => (
                                    <li key={i} className="text-sm text-emerald-700 flex items-center gap-2">
                                        <ChevronRight className="w-4 h-4" />
                                        {service}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
                            <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                                <AlertCircle className="w-5 h-5" />
                                Review These
                            </h4>
                            <ul className="space-y-2">
                                {serviceForecast?.recommendations?.review?.map((service, i) => (
                                    <li key={i} className="text-sm text-amber-700 flex items-center gap-2">
                                        <ChevronRight className="w-4 h-4" />
                                        {service}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-violet-50 rounded-2xl p-6 border border-violet-200">
                            <h4 className="font-bold text-violet-800 mb-3 flex items-center gap-2">
                                <Gift className="w-5 h-5" />
                                Bundle Opportunity
                            </h4>
                            <ul className="space-y-2">
                                {serviceForecast?.recommendations?.bundle?.map((service, i) => (
                                    <li key={i} className="text-sm text-violet-700 flex items-center gap-2">
                                        <ChevronRight className="w-4 h-4" />
                                        {service}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Seasonal Tab */}
            {activeTab === 'seasonal' && (
                <div className="space-y-6">
                    {/* Peak Times */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-6 text-white">
                            <Sun className="w-8 h-8 mb-3" />
                            <p className="text-sm opacity-80">Peak Hour</p>
                            <p className="text-3xl font-bold">{seasonalAnalysis?.peakHour}</p>
                        </div>
                        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white">
                            <CalendarDays className="w-8 h-8 mb-3" />
                            <p className="text-sm opacity-80">Peak Day</p>
                            <p className="text-3xl font-bold">{seasonalAnalysis?.peakDay}</p>
                        </div>
                        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
                            <CalendarRange className="w-8 h-8 mb-3" />
                            <p className="text-sm opacity-80">Peak Month</p>
                            <p className="text-3xl font-bold">{seasonalAnalysis?.peakMonth}</p>
                        </div>
                    </div>

                    {/* Hourly & Daily Trends */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Hourly Traffic Pattern</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <AreaChart data={seasonalAnalysis?.hourlyTrend || []}>
                                    <defs>
                                        <linearGradient id="colorHourly" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="hour" tick={{ fill: '#6b7280', fontSize: 10 }} interval={2} />
                                    <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <Tooltip />
                                    <Area type="monotone" dataKey="visits" stroke="#F59E0B" fill="url(#colorHourly)" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Daily Distribution</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={seasonalAnalysis?.dailyTrend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="day" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                                    <Tooltip />
                                    <Bar dataKey="visits" fill="#6366F1" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Monthly Trend */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Monthly Pattern</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart data={seasonalAnalysis?.monthlyTrend || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                <YAxis yAxisId="left" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fill: '#6b7280', fontSize: 11 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar yAxisId="left" dataKey="visits" name="Visits" fill="#6366F1" radius={[4, 4, 0, 0]} />
                                <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" strokeWidth={2} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Recommendations */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
                            <h4 className="font-bold text-indigo-800 mb-2 flex items-center gap-2">
                                <Users2 className="w-5 h-5" />
                                Staffing Tip
                            </h4>
                            <p className="text-sm text-indigo-700">{seasonalAnalysis?.recommendations?.staffingTip}</p>
                        </div>
                        <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
                            <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                                <Gift className="w-5 h-5" />
                                Promotion Tip
                            </h4>
                            <p className="text-sm text-amber-700">{seasonalAnalysis?.recommendations?.promotionTip}</p>
                        </div>
                        <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
                            <h4 className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
                                <CalendarClock className="w-5 h-5" />
                                Peak Season
                            </h4>
                            <p className="text-sm text-emerald-700">{seasonalAnalysis?.recommendations?.peakSeasonTip}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Cash Flow Tab */}
            {activeTab === 'cashflow' && (
                <div className="space-y-6">
                    {/* Cash Flow KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <MetricCard
                            title="Total Income"
                            value={formatCurrency(cashFlowForecast?.totalIncome || 0)}
                            subtitle="All time"
                            icon={ArrowUpRight}
                            color="emerald"
                        />
                        <MetricCard
                            title="Total Expenses"
                            value={formatCurrency(cashFlowForecast?.totalExpense || 0)}
                            subtitle="All time"
                            icon={ArrowDownRight}
                            color="rose"
                        />
                        <MetricCard
                            title="Net Cash Flow"
                            value={formatCurrency(cashFlowForecast?.netCashFlow || 0)}
                            subtitle={cashFlowForecast?.healthStatus}
                            icon={Banknote}
                            color={cashFlowForecast?.netCashFlow >= 0 ? 'emerald' : 'rose'}
                        />
                        <MetricCard
                            title="Forecasted"
                            value={formatCurrency(cashFlowForecast?.forecastedNetFlow || 0)}
                            subtitle="Next 3 months"
                            icon={Target}
                            color="violet"
                        />
                    </div>

                    {/* Cash Flow Chart */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Cash Flow Trend & Forecast</h3>
                        <ResponsiveContainer width="100%" height={350}>
                            <ComposedChart data={cashFlowForecast?.cashFlowTrend || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                                <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fill: '#6b7280', fontSize: 11 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey="income" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="expense" name="Expense" fill="#EF4444" radius={[4, 4, 0, 0]} />
                                <Line type="monotone" dataKey="netFlow" name="Net Flow" stroke="#6366F1" strokeWidth={3} dot={{ r: 4 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Payment Methods */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Method Distribution</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={cashFlowForecast?.paymentMethodDistribution || []}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={90}
                                        paddingAngle={3}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {(cashFlowForecast?.paymentMethodDistribution || []).map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v) => formatCurrency(v)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Monthly Averages</h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <ArrowUpRight className="w-6 h-6 text-emerald-600" />
                                        <span className="font-medium text-gray-900">Avg Monthly Income</span>
                                    </div>
                                    <span className="text-xl font-bold text-emerald-600">
                                        {formatCurrency(cashFlowForecast?.avgMonthlyIncome || 0)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-rose-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <ArrowDownRight className="w-6 h-6 text-rose-600" />
                                        <span className="font-medium text-gray-900">Avg Monthly Expense</span>
                                    </div>
                                    <span className="text-xl font-bold text-rose-600">
                                        {formatCurrency(cashFlowForecast?.avgMonthlyExpense || 0)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <Banknote className="w-6 h-6 text-indigo-600" />
                                        <span className="font-medium text-gray-900">Avg Monthly Net</span>
                                    </div>
                                    <span className={`text-xl font-bold ${(cashFlowForecast?.avgMonthlyNetFlow || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {formatCurrency(cashFlowForecast?.avgMonthlyNetFlow || 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Risk Analysis Tab */}
            {activeTab === 'risks' && (
                <div className="space-y-6">
                    {/* Risk Score */}
                    <div className={`rounded-2xl p-8 ${riskAnalysis?.overallRiskLevel === 'High' ? 'bg-gradient-to-r from-rose-500 to-red-600' :
                        riskAnalysis?.overallRiskLevel === 'Medium' ? 'bg-gradient-to-r from-amber-500 to-orange-600' :
                            'bg-gradient-to-r from-emerald-500 to-teal-600'
                        } text-white`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Overall Business Risk</h2>
                                <p className="opacity-80">Based on {riskAnalysis?.risks?.length || 0} identified risk factors</p>
                            </div>
                            <div className="text-center">
                                <p className="text-6xl font-bold">{riskAnalysis?.riskScore || 0}%</p>
                                <p className="text-xl font-semibold">{riskAnalysis?.overallRiskLevel} Risk</p>
                            </div>
                        </div>
                    </div>

                    {/* Risk Details */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {riskAnalysis?.risks?.map((risk, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-6 shadow-sm border-l-4 ${risk.severity === 'High' ? 'border-rose-500' :
                                    risk.severity === 'Medium' ? 'border-amber-500' :
                                        'border-emerald-500'
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <h3 className="text-lg font-bold text-gray-900">{risk.type}</h3>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${risk.severity === 'High' ? 'bg-rose-100 text-rose-700' :
                                        risk.severity === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                            'bg-emerald-100 text-emerald-700'
                                        }`}>
                                        {risk.severity}
                                    </span>
                                </div>
                                <p className="text-gray-600 mb-4">{risk.description}</p>
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <p className="text-sm font-medium text-gray-700">
                                        <span className="text-indigo-600">Recommendation:</span> {risk.recommendation}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Opportunities */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-amber-500" />
                            Growth Opportunities
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {riskAnalysis?.opportunities?.map((opp, index) => (
                                <div key={index} className="p-4 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl border border-indigo-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="font-semibold text-gray-900">{opp.title}</h4>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${opp.potential === 'High' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                            }`}>
                                            {opp.potential} Potential
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600">{opp.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color }) => {
    const colorClasses = {
        indigo: 'from-indigo-500 to-indigo-600',
        emerald: 'from-emerald-500 to-emerald-600',
        violet: 'from-violet-500 to-violet-600',
        amber: 'from-amber-500 to-amber-600',
        rose: 'from-rose-500 to-rose-600',
    };

    return (
        <div className={`relative overflow-hidden bg-gradient-to-br ${colorClasses[color] || colorClasses.indigo} rounded-2xl p-5 text-white shadow-lg`}>
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full" />
            <div className="relative">
                <div className="flex items-center justify-between mb-3">
                    <Icon className="w-7 h-7 opacity-80" />
                    {trend !== null && trend !== undefined && (
                        <span className={`text-xs px-2 py-1 rounded-full ${trend >= 0 ? 'bg-white/20' : 'bg-white/20'}`}>
                            {trend >= 0 ? '+' : ''}{trend?.toFixed(1)}%
                        </span>
                    )}
                </div>
                <p className="text-2xl font-bold">{value}</p>
                <p className="text-sm opacity-80 mt-1">{subtitle}</p>
            </div>
        </div>
    );
};

export default BusinessForecasting;
